<template>
  <div class="min-h-screen p-4 sm:p-6 lg:p-8">
    <Banner @goClicked="handleGoClicked" />
    <div class="mx-auto p-6 lg:p-8">
      <!-- Initial Loading State (for the very first load of the page) -->
      <div
        v-if="isInitialLoading"
        class="flex flex-col items-center justify-center min-h-[60vh] px-4"
      >
        <!-- Modern loading animation -->
        <div class="relative mb-8">
          <!-- Outer ring -->
          <div class="w-20 h-20 border-4 border-slate-200 rounded-full"></div>
          <!-- Animated inner ring -->
          <div
            class="absolute inset-0 w-20 h-20 border-4 border-transparent border-t-blue-500 border-r-blue-400 rounded-full animate-spin"
          ></div>
          <!-- Center dot -->
          <div class="absolute inset-0 flex items-center justify-center">
            <div class="w-3 h-3 bg-blue-500 rounded-full animate-pulse"></div>
          </div>
        </div>

        <!-- Content with better typography -->
        <div class="text-center max-w-sm">
          <h3 class="text-2xl font-semibold text-slate-800 mb-2">
            Loading Teachers
          </h3>
          <p class="text-slate-600 leading-relaxed">
            We're gathering all the amazing educators for you. This will just
            take a moment.
          </p>

          <!-- Animated progress dots -->
          <div class="flex justify-center space-x-2 mt-6">
            <div
              class="w-2 h-2 bg-blue-400 rounded-full animate-bounce [animation-delay:-0.3s]"
            ></div>
            <div
              class="w-2 h-2 bg-blue-500 rounded-full animate-bounce [animation-delay:-0.15s]"
            ></div>
            <div class="w-2 h-2 bg-blue-600 rounded-full animate-bounce"></div>
          </div>
        </div>
      </div>
      <!-- Main Content (only show when not initially loading) -->
      <div v-else>
        <!-- Header - This component is responsible for the bulk delete button -->
        <HeaderTeacher
          :bannerGoClicked="bannerClicked"
          @search="searchQuery = $event"
          @add-new="addTeacher"
          @bulk-delete="bulkDeleteTeachers"
          :is-any-selected="isAnyTeacherSelected"
          :selected-count="selectedTeacherIds.length"
        />
        <!-- Bulk Delete Button (appears when teachers are selected) -->
        <div
          v-if="isAnyTeacherSelected"
          class="mt-6 mb-4 flex flex-col sm:flex-row sm:justify-end sm:items-center gap-3 sm:gap-4 animate-fade-in"
        >
          <!-- 🟦 Order Cards Button -->
          <button
            @click="bulkOrderCards"
            :disabled="isLoading || isTableLoading"
            class="inline-flex items-center justify-center w-full sm:w-auto px-5 py-2.5 border border-transparent text-base font-medium rounded-xl shadow-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <CreditCard class="w-5 h-5 mr-2" />
            Order Cards ({{ selectedTeacherIds.length }})
          </button>

          <!-- 🟥 Delete Selected Button -->
          <button
            @click="bulkDeleteTeachers"
            :disabled="isLoading || isTableLoading"
            class="inline-flex items-center justify-center w-full sm:w-auto px-5 py-2.5 border border-transparent text-base font-medium rounded-xl shadow-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Trash2 class="w-5 h-5 mr-2" />
            Delete Selected ({{ selectedTeacherIds.length }})
          </button>
        </div>

        <!-- Teachers Table & Grid Container -->
        <div class="relative">
          <!-- Content Loading Overlay (for search, pagination, refresh) -->
          <div
            v-if="isTableLoading"
            class="absolute inset-0 bg-white/60 backdrop-blur-sm rounded-xl z-10 flex items-center justify-center"
          >
            <div class="text-center">
              <div
                class="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-2"
              ></div>
              <p class="text-sm text-gray-600">Loading teachers...</p>
            </div>
          </div>

          <!-- Teachers Table (Desktop View) -->
          <div
            class="hidden md:block bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden"
            :class="{ 'mt-6': !isAnyTeacherSelected }"
          >
            <div
              class="bg-gray-600 px-3 sm:px-4 md:px-6 py-3 sm:py-4 border-b border-gray-700"
            >
              <div class="flex items-center justify-between">
                <div class="flex-1 min-w-0">
                  <h3
                    class="text-base sm:text-lg md:text-xl font-bold text-white truncate"
                  >
                    Teacher Directory
                  </h3>
                  <p class="text-gray-300 text-xs sm:text-sm mt-0.5 sm:mt-1">
                    Manage and view all teacher information
                  </p>
                </div>
              </div>
            </div>
            <div class="overflow-x-auto">
              <table class="min-w-full divide-y divide-gray-200">
                <thead class="bg-gray-50">
                  <tr>
                    <th
                      class="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider"
                    >
                      <!-- Checkbox for selecting all visible teachers -->
                      <input
                        type="checkbox"
                        :checked="allSelected"
                        @change="toggleSelectAll"
                        :disabled="isLoading || isTableLoading"
                        class="rounded border-gray-300 text-blue-600 shadow-sm focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed h-4 w-4"
                      />
                    </th>
                    <th
                      class="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider"
                    >
                      <div class="flex items-center gap-2">
                        <UserRound
                          class="w-4 h-4 text-blue-600"
                          aria-hidden="true"
                        />
                        <span>Photo</span>
                      </div>
                    </th>
                    <th
                      class="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider"
                    >
                      <div class="flex items-center gap-2">
                        <UserRound
                          class="w-4 h-4 text-blue-600"
                          aria-hidden="true"
                        />
                        <span>Name</span>
                      </div>
                    </th>
                    <th
                      class="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider"
                    >
                      <div class="flex items-center gap-2">
                        <Briefcase
                          class="w-4 h-4 text-blue-600"
                          aria-hidden="true"
                        />
                        <span>Role</span>
                      </div>
                    </th>
                    <th
                      class="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider"
                    >
                      <div class="flex items-center gap-2">
                        <span>&nbsp;</span>
                        <BookOpen
                          class="w-4 h-4 text-blue-600"
                          aria-hidden="true"
                        />
                        <span>Subject</span>
                      </div>
                    </th>
                    <th
                      class="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider"
                    >
                      <div class="flex items-center gap-2">
                        <Mail
                          class="w-4 h-4 text-blue-600"
                          aria-hidden="true"
                        />
                        <span>Email</span>
                      </div>
                    </th>
                    <th
                      class="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider"
                    >
                      <div class="flex items-center gap-2">
                        <Phone
                          class="w-4 h-4 text-blue-600"
                          aria-hidden="true"
                        />
                        <span>Phone</span>
                      </div>
                    </th>

                    <th
                      class="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider"
                    >
                      <div class="flex items-center gap-2">
                        <span>&nbsp;</span>
                        <CircleDot
                          class="w-4 h-4 text-blue-600"
                          aria-hidden="true"
                        />
                        <span>Status</span>
                      </div>
                    </th>
                    <th
                      class="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider"
                    >
                      <div class="flex items-center gap-2">
                        <span> &nbsp;</span>
                        <Edit
                          class="w-4 h-4 text-blue-600"
                          aria-hidden="true"
                        />
                        <span>Actions</span>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-100">
                  <tr
                    v-for="teacher in teachers"
                    :key="teacher._id"
                    class="hover:bg-gray-50 transition-colors duration-150"
                  >
                    <td class="px-4 py-3 whitespace-nowrap">
                      <!-- Checkbox for individual teacher selection -->
                      <input
                        type="checkbox"
                        :checked="selectedTeacherIds.includes(teacher._id)"
                        @change="toggleTeacherSelection(teacher._id)"
                        :disabled="isLoading || isTableLoading"
                        class="rounded border-gray-300 text-blue-600 shadow-sm focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed h-4 w-4"
                      />
                    </td>
                    <td
                      class="px-4 py-3 whitespace-nowrap cursor-pointer"
                      @click="viewTeacher(teacher)"
                    >
                      <div
                        class="w-10 h-10 rounded-full flex items-center justify-center font-bold text-white border-2"
                        :class="[
                          teacher.status === 'active'
                            ? 'border-green-500'
                            : 'border-red-500',
                          teacher.photo ? '' : getAvatarColor(teacher.name),
                        ]"
                      >
                        <img
                          v-if="teacher.photo"
                          :src="teacher.photo"
                          :alt="teacher.name"
                          class="w-full h-full rounded-full object-cover"
                        />
                        <span
                          v-else
                          class="text-sm"
                        >
                          {{ getInitial(teacher.name) }}
                        </span>
                      </div>
                    </td>
                    <td
                      class="px-4 py-3 whitespace-nowrap cursor-pointer"
                      @click="viewTeacher(teacher)"
                    >
                      <div
                        class="flex items-center gap-2 text-sm font-medium text-gray-900"
                      >
                        <UserRound
                          class="w-4 h-4 text-blue-600"
                          aria-hidden="true"
                        />
                        <span>{{ teacher.name }}</span>
                      </div>
                    </td>
                    <td class="px-4 py-3 whitespace-nowrap">
                      <span
                        class="inline-flex items-center gap-1 text-sm text-gray-800"
                      >
                        <Briefcase
                          class="w-4 h-4 text-blue-600"
                          aria-hidden="true"
                        />
                        {{ teacher.role }}
                      </span>
                    </td>
                    <td class="px-4 py-3 whitespace-nowrap">
                      <span
                        class="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700"
                      >
                        <BookOpen
                          class="w-4 h-4 text-blue-600"
                          aria-hidden="true"
                        />
                        {{ teacher.subject }}
                      </span>
                    </td>
                    <td class="px-4 py-3 whitespace-nowrap">
                      <span
                        class="inline-flex items-center gap-1 text-[12px] text-gray-800"
                      >
                        <Mail
                          class="w-4 h-4 text-blue-600"
                          aria-hidden="true"
                        />
                        {{ teacher.email }}
                      </span>
                    </td>

                    <td class="px-4 py-3 whitespace-nowrap">
                      <span
                        class="inline-flex items-center gap-1 text-sm text-gray-800"
                      >
                        <Phone
                          class="w-4 h-4 text-blue-600"
                          aria-hidden="true"
                        />
                        {{ teacher.phone }}
                      </span>
                    </td>

                    <td class="px-4 py-3 whitespace-nowrap">
                      <span
                        :class="
                          teacher.status === 'active'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        "
                        class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
                      >
                        <CircleDot
                          class="w-4 h-4"
                          :class="
                            teacher.status === 'active'
                              ? 'text-green-600'
                              : 'text-red-600'
                          "
                          aria-hidden="true"
                        />
                        {{
                          teacher.status === "passout"
                            ? "inactive"
                            : teacher.status
                        }}
                      </span>
                    </td>
                    <td
                      class="px-4 py-3 whitespace-nowrap text-right text-sm font-medium"
                    >
                      <div class="flex items-center gap-1">
                        <button
                          @click="viewTeacher(teacher)"
                          :disabled="isLoading || isTableLoading"
                          class="text-gray-500 hover:text-blue-600 p-2 rounded-full hover:bg-blue-50 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                          title="View Details"
                        >
                          <Eye class="w-4 h-4" />
                        </button>
                        <button
                          @click="editTeacher(teacher)"
                          :disabled="isLoading || isTableLoading"
                          class="text-gray-500 hover:text-green-600 p-2 rounded-full hover:bg-green-50 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Edit Teacher"
                        >
                          <Edit class="w-4 h-4" />
                        </button>
                        <button
                          @click="deleteTeacher(teacher)"
                          :disabled="isLoading || isTableLoading"
                          class="text-gray-500 hover:text-red-600 p-2 rounded-full hover:bg-red-50 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Delete Teacher"
                        >
                          <Trash2 class="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                  <tr v-if="teachers.length === 0 && !isTableLoading">
                    <td
                      colspan="10"
                      class="px-4 py-6 text-center text-gray-500 text-base"
                    >
                      No teachers found.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <!-- Teachers Grid (Mobile View) -->
          <div
            class="md:hidden grid grid-cols-1 sm:grid-cols-2 gap-4 w-full"
            :class="{ 'mt-6': !isAnyTeacherSelected }"
          >
            <div
              v-if="teachers.length === 0 && !isTableLoading"
              class="col-span-full text-center py-6 text-gray-500 bg-white rounded-xl shadow-sm border border-gray-100 text-base"
            >
              No teachers found.
            </div>
            <div
              v-for="teacher in teachers"
              :key="teacher._id"
              class="bg-white border border-gray-100 p-4 flex flex-col space-y-3 w-full max-w-2xl backdrop-blur rounded-2xl sm:rounded-3xl shadow-xl border-white/20"
            >
              <div class="flex items-center justify-between">
                <div class="flex items-center space-x-3">
                  <div
                    @click="viewTeacher(teacher)"
                    class="w-16 h-16 rounded-full flex items-center justify-center font-bold text-white text-xl border-2"
                    :class="[
                      teacher.status === 'active'
                        ? 'border-green-500'
                        : 'border-red-500',
                      teacher.photo ? '' : getAvatarColor(teacher.name),
                    ]"
                  >
                    <img
                      v-if="teacher.photo"
                      :src="teacher.photo"
                      :alt="teacher.name"
                      class="w-full h-full rounded-full object-cover"
                    />
                    <span v-else>
                      {{ getInitial(teacher.name) }}
                    </span>
                  </div>

                  <div>
                    <h3
                      class="flex items-center gap-2 text-lg font-semibold text-gray-900"
                      @click="viewTeacher(teacher)"
                    >
                      <UserRound
                        class="w-5 h-5 text-blue-600"
                        aria-hidden="true"
                      />
                      {{ teacher.name }}
                    </h3>
                    <p class="text-gray-500 text-[10px]">
                      ID: {{ teacher.attendanceID }}
                    </p>
                  </div>
                </div>
                <!-- Checkbox for individual teacher selection -->
                <input
                  type="checkbox"
                  :checked="selectedTeacherIds.includes(teacher._id)"
                  @change="toggleTeacherSelection(teacher._id)"
                  :disabled="isLoading || isTableLoading"
                  class="rounded border-gray-300 text-blue-600 shadow-sm focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed h-5 w-5"
                />
              </div>
              <div
                class="grid grid-cols-2 gap-y-2 text-sm text-gray-800"
                @click="viewTeacher(teacher)"
              >
                <div class="flex items-center gap-1">
                  <Briefcase
                    class="w-4 h-4 text-blue-600"
                    aria-hidden="true"
                  />
                  <span class="text-gray-700 font-semibold">Role:</span>
                  <span class="ml-1">{{ teacher.role }}</span>
                </div>
                <div class="flex items-center gap-1">
                  <BookOpen
                    class="w-4 h-4 text-blue-600"
                    aria-hidden="true"
                  />
                  <span class="font-semibold text-gray-700">Subject:</span>
                  <span
                    class="ml-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 whitespace-nowrap max-w-[120px] truncate"
                  >
                    {{ teacher.subject }}
                  </span>
                </div>
                <div class="col-span-2 flex items-center gap-1">
                  <Mail
                    class="w-4 h-4 text-blue-600"
                    aria-hidden="true"
                  />
                  <span class="font-semibold text-gray-700">Email:</span>
                  <span class="ml-1 break-all">{{ teacher.email }}</span>
                </div>
                <div class="flex items-center gap-1">
                  <Phone
                    class="w-4 h-4 text-blue-600"
                    aria-hidden="true"
                  />
                  <span class="font-semibold text-gray-700">Phone:</span>
                  <span class="ml-1">{{ teacher.phone }}</span>
                </div>
                <div class="flex items-center gap-1">
                  <Calendar
                    class="w-4 h-4 text-blue-600"
                    aria-hidden="true"
                  />
                  <span class="font-semibold text-gray-700">DOB:</span>
                  <span class="ml-1 text-[11px] sm:text-sm">
                    {{ formatDate(teacher.dob) }}
                  </span>
                </div>
                <div class="flex items-center gap-1">
                  <CircleDot
                    class="w-4 h-4"
                    :class="
                      teacher.status === 'active'
                        ? 'text-green-600'
                        : 'text-red-600'
                    "
                    aria-hidden="true"
                  />
                  <span class="font-medium text-gray-700">Status:</span>
                  <span
                    :class="
                      teacher.status === 'active'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    "
                    class="ml-1 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                  >
                    {{
                      teacher.status === "passout" ? "inactive" : teacher.status
                    }}
                  </span>
                </div>
              </div>
              <div
                class="flex justify-end gap-2 mt-4 border-t pt-3 border-gray-100"
              >
                <button
                  @click="viewTeacher(teacher)"
                  :disabled="isLoading || isTableLoading"
                  class="text-gray-500 hover:text-blue-600 p-2 rounded-full hover:bg-blue-50 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="View Details"
                >
                  <Eye class="w-5 h-5" />
                </button>
                <button
                  @click="editTeacher(teacher)"
                  :disabled="isLoading || isTableLoading"
                  class="text-gray-500 hover:text-green-600 p-2 rounded-full hover:bg-green-50 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Edit Teacher"
                >
                  <Edit class="w-5 h-5" />
                </button>
                <button
                  @click="deleteTeacher(teacher)"
                  :disabled="isLoading || isTableLoading"
                  class="text-gray-500 hover:text-red-600 p-2 rounded-full hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200 shadow-sm"
                >
                  <Trash2 class="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
        <!-- Pagination Controls -->
        <div
          v-if="totalResults > 0 && totalPages > 1"
          class="flex justify-center items-center space-x-3 mt-8"
        >
          <button
            @click="goToPage(currentPage - 1)"
            :disabled="currentPage === 1 || isLoading || isTableLoading"
            class="inline-flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 shadow-sm"
          >
            <ChevronLeft
              class="w-4 h-4 text-blue-600"
              aria-hidden="true"
            />
            <span>Previous</span>
          </button>
          <span class="text-gray-700 font-medium">
            Page {{ currentPage }} of {{ totalPages }}
          </span>
          <button
            @click="goToPage(currentPage + 1)"
            :disabled="
              currentPage === totalPages || isLoading || isTableLoading
            "
            class="inline-flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 shadow-sm"
          >
            <span>Next</span>
            <ChevronRight
              class="w-4 h-4 text-blue-600"
              aria-hidden="true"
            />
          </button>
        </div>
        <!-- Delete Confirmation Modal (Inline for simplicity, consider a dedicated modal component for complex UIs) -->
        <div
          v-if="showDeleteModal"
          class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70 p-4 sm:p-6"
          @click.self="cancelDelete"
        >
          <div
            class="bg-white rounded-xl shadow-2xl border border-gray-100 p-6 w-full max-w-md transform transition-all duration-300 ease-in-out scale-95 opacity-0"
            :class="{ 'scale-100 opacity-100': showDeleteModal }"
          >
            <h2 class="text-2xl font-bold text-gray-900 mb-4">
              Confirm Deletion
            </h2>
            <p
              v-if="teacherToDelete"
              class="text-gray-700 mb-6"
            >
              Are you sure you want to delete teacher
              <span class="font-semibold text-red-600">{{
                teacherToDelete.name
              }}</span>
            </p>
            <p
              v-else
              class="text-gray-700 mb-6"
            >
              Are you sure you want to delete the selected
              <span class="font-semibold text-red-600">{{
                selectedTeacherIds.length
              }}</span>
              teachers? This action cannot be undone.
            </p>
            <div class="flex justify-end space-x-3">
              <button
                @click="cancelDelete"
                :disabled="isLoading"
                class="inline-flex justify-center py-2.5 px-5 border border-gray-200 shadow-sm text-base font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                @click="confirmDelete"
                :disabled="isLoading"
                class="inline-flex justify-center py-2.5 px-5 border border-transparent shadow-sm text-base font-medium rounded-lg text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span
                  v-if="isLoading"
                  class="flex items-center"
                >
                  <svg
                    class="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      class="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      stroke-width="4"
                    ></circle>
                    <path
                      class="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Deleting...
                </span>
                <span v-else>Delete</span>
              </button>
            </div>
          </div>
        </div>
        <!-- 🟦 Order Cards Confirmation Modal -->
        <div
          v-if="showOrderModal"
          class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70 p-4 sm:p-6"
          @click.self="cancelOrderCards"
        >
          <div
            class="bg-white rounded-xl shadow-2xl border border-gray-100 p-6 w-full max-w-md transform transition-all duration-300 ease-in-out scale-95 opacity-0"
            :class="{ 'scale-100 opacity-100': showOrderModal }"
          >
            <h2 class="text-2xl font-bold text-gray-900 mb-4">
              Confirm Smart Card Order
            </h2>
            <!-- Important notice box -->
            <div class="bg-blue-50 border border-blue-200 rounded-md p-3">
              <p class="text-sm text-blue-900">
                <span class="font-semibold">⚠️ Important:</span> Only teacher
                with status
                <span class="font-bold text-blue-700">ACTIVE</span> card will be
                booked.
              </p>
            </div>
            <p class="text-gray-700 mb-6 text-sm">
              Are you sure you want to order
              <span class="font-semibold text-blue-600">{{
                selectedTeacherIds.length
              }}</span>
              smart card(s) for the selected teachers?
            </p>
            <div class="flex justify-end space-x-3">
              <button
                @click="cancelOrderCards"
                :disabled="isLoading"
                class="inline-flex justify-center py-2.5 px-5 border border-gray-200 shadow-sm text-base font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                @click="confirmOrderCards"
                :disabled="isLoading"
                class="inline-flex justify-center py-2.5 px-5 border border-transparent shadow-sm text-base font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span
                  v-if="isLoading"
                  class="flex items-center"
                >
                  <svg
                    class="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      class="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      stroke-width="4"
                    ></circle>
                    <path
                      class="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Ordering...
                </span>
                <span v-else>Confirm Order</span>
              </button>
            </div>
          </div>
        </div>
      </div>
      <!-- Toast Notification (Integrated) -->
      <transition name="fade">
        <div
          v-if="toast.isVisible"
          :class="toastClasses"
          class="fixed bottom-6 right-6 p-4 rounded-lg shadow-xl text-white text-base font-medium flex items-center space-x-2 min-w-[250px] z-[100000]"
        >
          <Info
            v-if="toast.type === 'info'"
            class="w-5 h-5"
          />
          <CheckCircle
            v-if="toast.type === 'success'"
            class="w-5 h-5"
          />
          <XCircle
            v-if="toast.type === 'error'"
            class="w-5 h-5"
          />
          <span>{{ toast.message }}</span>
        </div>
      </transition>
      <!-- Teacher Details Modal -->
      <TeacherDetailsModal
        :teacher="selectedTeacher"
        :is-open="showViewModal"
        @close="showViewModal = false"
      />
      <!-- Teacher Edit Modal -->
      <TeacherEditModal
        :teacher="selectedTeacher"
        :is-open="showEditModal"
        @close="showEditModal = false"
        @save="handleSaveEdit"
        :isLoading="isLoading"
      />
    </div>
  </div>
</template>

<script setup>
  import { ref, computed, reactive, onMounted, watch } from "vue";
  import { useStore } from "vuex";
  import { useRoute } from "vue-router";
  import {
    Eye,
    Edit,
    Trash2,
    Info,
    CheckCircle,
    XCircle,
    Briefcase,
    BookOpen,
    Mail,
    Phone,
    CreditCard,
    CircleDot,
    ChevronLeft,
    ChevronRight,
    UserRound,
  } from "lucide-vue-next";
  import HeaderTeacher from "@/components/Teacher/HeaderTeacher.vue";
  import TeacherDetailsModal from "@/components/Teacher/Actions/TeacherDetailsModal.vue";
  import TeacherEditModal from "@/components/Teacher/Actions/TeacherEditModal.vue";
  import Banner from "@/components/Banner/TeacherBanner.vue";
  const bannerClicked = ref(false);
  function handleGoClicked(value) {
    bannerClicked.value = value;
  }

  // Vuex store
  const store = useStore();
  const route = useRoute();
  // UI state
  const searchQuery = ref("");
  const showModal = ref(false);
  const showViewModal = ref(false);
  const showEditModal = ref(false);
  const showDeleteModal = ref(false);
  const showOrderModal = ref(false);

  const isEditing = ref(false);
  const selectedTeacher = ref(null);
  const teacherToDelete = ref(null);
  const selectedTeacherIds = ref([]);
  const isLoading = ref(false); // This ref controls the loading state for save/delete operations
  const isInitialLoading = ref(true); // For the very first page load
  const isTableLoading = ref(false); // For search, pagination, and refresh of the table content

  // Pagination state
  const currentPage = ref(1);
  const itemsPerPage = ref(10);

  const toast = reactive({
    isVisible: false,
    message: "",
    type: "info",
  });
  let toastTimeoutId = null;

  // Form data (currentTeacher is now less critical as edit modal manages its own local state)
  const currentTeacher = reactive({
    id: "",
    name: "",
    role: "teacher",
    subject: "",
    email: "",
    phone: "",
    status: "active",
    photo: "",
    dob: "",
  });

  // Helper to format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch (e) {
      return dateString;
    }
  };

  // Fetch teachers with pagination and search parameters
  const fetchTeachers = async (
    page = currentPage.value,
    limit = itemsPerPage.value,
    search = searchQuery.value,
  ) => {
    // Set loading state based on whether it's the initial load or a content refresh
    if (isInitialLoading.value) {
      // Keep isInitialLoading true
    } else {
      isTableLoading.value = true; // For subsequent loads (search, pagination)
    }

    try {
      currentPage.value = page; // Update current page before dispatching
      await store.dispatch("fetchTeachers", { page, limit, search });
    } catch (error) {
      console.error("Error fetching teachers:", error);
      showToast("❌ Failed to load teachers", "error");
    } finally {
      isInitialLoading.value = false; // Always set to false after first load attempt
      isTableLoading.value = false; // Always set to false after content load attempt
    }
  };

  onMounted(async () => {
    await fetchTeachers(
      currentPage.value,
      itemsPerPage.value,
      searchQuery.value,
    );

    // 🔥 If coming from Smart Card page
    const userId = route.query.userId;

    if (userId) {
      const teacher = teachers.value.find((t) => t._id === userId);

      if (teacher) {
        // Already in list → open modal
        selectedTeacher.value = teacher;
        showViewModal.value = true;
      } else {
        // Not in current page → fetch directly
        const res = await store.dispatch("fetchUserById", userId);

        selectedTeacher.value = res;
        showViewModal.value = true;
      }
    }
  });

  // Get teachers and pagination info from Vuex
  const teachers = computed(() => store.getters.getAllTeachers || []);
  const totalResults = computed(
    () => store.getters.getTeacherPagination?.totalResults || 0,
  );
  const totalPages = computed(
    () => store.getters.getTeacherPagination?.totalPages || 0,
  );

  // Watch for search query changes and refetch data
  let searchTimeout = null;
  watch(searchQuery, (newQuery) => {
    clearTimeout(searchTimeout);
    isTableLoading.value = true; // Show loading immediately on search input
    searchTimeout = setTimeout(() => {
      fetchTeachers(1, itemsPerPage.value, newQuery); // Reset to page 1 on new search
    }, 300); // Debounce search input
  });

  // Watch for changes in teachers data (e.g., after delete/edit) to clear selections
  watch(teachers, () => {
    selectedTeacherIds.value = [];
  });

  // Pagination actions
  const goToPage = (page) => {
    if (
      page >= 1 &&
      page <= totalPages.value &&
      !isLoading.value &&
      !isTableLoading.value
    ) {
      fetchTeachers(page, itemsPerPage.value, searchQuery.value);
    }
  };

  // Checkbox logic for current page
  const allSelected = computed(() => {
    return (
      teachers.value.length > 0 &&
      teachers.value.every((t) => selectedTeacherIds.value.includes(t._id))
    );
  });

  const isAnyTeacherSelected = computed(
    () => selectedTeacherIds.value.length > 0,
  );

  // Toast classes
  const toastClasses = computed(() => {
    const baseClasses = "transition-all duration-300 ease-in-out";
    switch (toast.type) {
      case "success":
        return `${baseClasses} bg-green-600`;
      case "error":
        return `${baseClasses} bg-red-600`;
      case "info":
      default:
        return `${baseClasses} bg-blue-600`;
    }
  });

  // Toast utility
  const showToast = (msg, type = "info", duration = 3000) => {
    if (toastTimeoutId) clearTimeout(toastTimeoutId);
    toast.message = msg;
    toast.type = type;
    toast.isVisible = true;
    toastTimeoutId = setTimeout(() => {
      toast.isVisible = false;
    }, duration);
  };

  // Reset form
  const resetCurrentTeacher = () => {
    Object.assign(currentTeacher, {
      id: "",
      name: "",
      role: "teacher",
      subject: "",
      email: "",
      phone: "",
      status: "active",
      photo: "",
      dob: "",
    });
  };

  // Actions
  const addTeacher = () => {
    isEditing.value = false;
    resetCurrentTeacher();
    showModal.value = true;
  };

  const editTeacher = (teacher) => {
    selectedTeacher.value = teacher;
    showEditModal.value = true;
  };

  const viewTeacher = (teacher) => {
    selectedTeacher.value = teacher;
    showViewModal.value = true;
  };

  const deleteTeacher = (teacher) => {
    teacherToDelete.value = teacher;
    showDeleteModal.value = true;
  };

  const bulkDeleteTeachers = () => {
    teacherToDelete.value = null;
    showDeleteModal.value = true;
  };

  const confirmDelete = async () => {
    isLoading.value = true;
    try {
      if (teacherToDelete.value) {
        await store.dispatch("deleteUserById", teacherToDelete.value._id);
        showToast(`🗑️ Deleted: ${teacherToDelete.value.name}`, "success");
      } else if (selectedTeacherIds.value.length > 0) {
        for (const id of selectedTeacherIds.value) {
          await store.dispatch("deleteUserById", id);
        }
        showToast(
          `🗑️ ${selectedTeacherIds.value.length} teachers deleted`,
          "success",
        );
        selectedTeacherIds.value = [];
      }
      showDeleteModal.value = false;
      await fetchTeachers(
        currentPage.value,
        itemsPerPage.value,
        searchQuery.value,
      ); // Re-fetch current page after delete
    } catch (err) {
      showToast("❌ Failed to delete teacher(s)", "error");
    } finally {
      isLoading.value = false;
      teacherToDelete.value = null;
    }
  };

  const cancelDelete = () => {
    showDeleteModal.value = false;
    teacherToDelete.value = null;
  };
  const bulkOrderCards = () => {
    if (selectedTeacherIds.value.length === 0) {
      showToast("Please select at least one teacher", "info");
      return;
    }
    showOrderModal.value = true; // Show confirmation modal
  };
  const confirmOrderCards = async () => {
    isLoading.value = true;
    try {
      await store.dispatch("bookSmartCard", {
        userId: selectedTeacherIds.value,
      });

      showToast(
        `✅ Smart cards ordered for ${selectedTeacherIds.value.length} teacher(s)`,
        "success",
      );
      selectedTeacherIds.value = [];
      showOrderModal.value = false;
      await fetchTeachers(
        currentPage.value,
        itemsPerPage.value,
        searchQuery.value,
      );
    } catch (error) {
      console.error("Error ordering smart cards:", error);
      const backendError = error.response?.data?.error;
      if (backendError) {
        showToast(`❌ ${backendError}`, "error");
      } else {
        showToast("❌ Failed to order smart cards", "error");
      }
    } finally {
      isLoading.value = false;
    }
  };

  const cancelOrderCards = () => {
    showOrderModal.value = false;
  };

  // Handle save from TeacherEditModal
  const handleSaveEdit = async ({ teacherData }) => {
    isLoading.value = true; // Set loading true when save starts
    try {
      const originalTeacher = selectedTeacher.value;
      const updatesToSend = {};

      // Helper to normalize values that might be empty objects to empty strings
      const normalizeValue = (value) => {
        return typeof value === "object" &&
          value !== null &&
          Object.keys(value).length === 0
          ? "" // Treat empty object as empty string
          : value;
      };

      // 1. Handle regular fields (non-file related)
      for (const key in teacherData) {
        if (
          key === "_id" ||
          key === "photo" ||
          key === "aadhaarCard" ||
          key === "qualifications"
        ) {
          continue; // Handle these separately
        }
        const normalizedNewValue = normalizeValue(teacherData[key]);
        if (normalizedNewValue !== originalTeacher[key]) {
          updatesToSend[key] = normalizedNewValue;
        }
      }

      // 2. Handle photo (now expected to be a Base64 string or URL in teacherData.photo)
      const normalizedNewPhotoValue = normalizeValue(teacherData.photo);
      if (normalizedNewPhotoValue !== originalTeacher.photo) {
        updatesToSend.photo = normalizedNewPhotoValue;
      }

      // 3. Handle aadhaarCard (now expected to be a Base64 string or URL in teacherData.aadhaarCard)
      const normalizedNewAadhaarValue = normalizeValue(teacherData.aadhaarCard);
      if (normalizedNewAadhaarValue !== originalTeacher.aadhaarCard) {
        updatesToSend.aadhaarCard = normalizedNewAadhaarValue;
      }

      // 4. Handle qualifications
      const updatedQualifications = teacherData.qualifications
        ? JSON.parse(JSON.stringify(teacherData.qualifications))
        : [];
      let qualificationsChanged = false;

      // Normalize fileUrl for each qualification and check for changes
      updatedQualifications.forEach((qual, index) => {
        const normalizedFileUrl = normalizeValue(qual.fileUrl);
        // Compare with original qualification's fileUrl
        if (
          originalTeacher.qualifications &&
          originalTeacher.qualifications[index]
        ) {
          if (
            normalizedFileUrl !== originalTeacher.qualifications[index].fileUrl
          ) {
            qual.fileUrl = normalizedFileUrl;
            qualificationsChanged = true;
          }
        } else if (normalizedFileUrl !== "") {
          // New qualification with a fileUrl
          qualificationsChanged = true;
        }
      });

      // Check if the array structure itself changed (e.g., added/removed qualifications)
      if (
        updatedQualifications.length !==
        (originalTeacher.qualifications
          ? originalTeacher.qualifications.length
          : 0)
      ) {
        qualificationsChanged = true;
      }

      // If qualificationsChanged is true OR the array content is different, include it.
      if (
        qualificationsChanged ||
        JSON.stringify(updatedQualifications) !==
          JSON.stringify(originalTeacher.qualifications)
      ) {
        updatesToSend.qualifications = updatedQualifications;
      }

      // If no updates were detected, show an info toast and exit
      if (Object.keys(updatesToSend).length === 0) {
        showToast("ℹ️ No changes detected.", "info");
        showEditModal.value = false;
        return; // Exit early, no need to set isLoading to false in finally if we return here
      }

      await store.dispatch("editTeacherById", {
        teacherId: originalTeacher._id,
        updates: updatesToSend,
      });

      showToast(
        `✅ Teacher ${originalTeacher.name} updated successfully!`,
        "success",
      );
      await fetchTeachers(
        currentPage.value,
        itemsPerPage.value,
        searchQuery.value,
      );
    } catch (error) {
      console.error("Error saving teacher:", error);

      // 🔹 Show backend error (like email/phone already exists)
      const backendError = error.response?.data?.error;
      if (backendError) {
        showToast(`❌ ${backendError}`, "error");
      } else {
        showToast("❌ Failed to save teacher updates", "error");
      }
    } finally {
      isLoading.value = false; // Set loading false when save completes (success or error)
      showEditModal.value = false;
      selectedTeacher.value = null;
    }
  };

  // Toggle selection for all teachers on the current page
  const toggleSelectAll = () => {
    if (allSelected.value) {
      selectedTeacherIds.value = [];
    } else {
      selectedTeacherIds.value = teachers.value.map((t) => t._id);
    }
  };

  // Toggle selection for an individual teacher
  const toggleTeacherSelection = (id) => {
    const index = selectedTeacherIds.value.indexOf(id);
    if (index === -1) {
      selectedTeacherIds.value.push(id);
    } else {
      selectedTeacherIds.value.splice(index, 1);
    }
  };
  const getInitial = (name) => {
    if (!name) return "?";
    return name.charAt(0).toUpperCase();
  };
  const getAvatarColor = (name) => {
    const colors = [
      "bg-blue-500",
      "bg-green-500",
      "bg-purple-500",
      "bg-pink-500",
      "bg-indigo-500",
      "bg-amber-500",
    ];
    if (!name) return "bg-gray-400";
    return colors[name.charCodeAt(0) % colors.length];
  };
</script>

<style scoped>
  /* Toast transition styles */
  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.3s ease, transform 0.3s ease;
  }
  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
    transform: translateY(20px);
  }
  /* Basic spinner animation */
  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
  .animate-spin {
    animation: spin 1s linear infinite;
  }
  /* Fade-in animation for bulk delete button */
  @keyframes fade-in {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  .animate-fade-in {
    animation: fade-in 0.3s ease-out forwards;
  }
</style>
